import React from 'react';
import { Link } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Shared, cyan-forward design primitives for the marketing pages.
// Tailwind is now compiled app-wide (see tailwind.config.js).
// ---------------------------------------------------------------------------

const cx = (...c) => c.filter(Boolean).join(' ');

export const Section = ({ id, className = '', children, container = true }) => (
  <section id={id} className={cx('relative px-4 py-16 md:py-24', className)}>
    {container ? <div className="mx-auto w-full max-w-6xl">{children}</div> : children}
  </section>
);

export const Eyebrow = ({ children, className = '' }) => (
  <span
    className={cx(
      'inline-block rounded-full border border-brand/30 bg-brand/10 px-3 py-1',
      'text-xs font-semibold uppercase tracking-wider text-brand-light',
      className
    )}
  >
    {children}
  </span>
);

export const SectionHeading = ({ eyebrow, title, subtitle, align = 'center', className = '' }) => (
  <div className={cx(align === 'center' ? 'text-center mx-auto' : 'text-left', 'max-w-3xl', align === 'center' && 'mx-auto', className)}>
    {eyebrow && <div className="mb-4"><Eyebrow>{eyebrow}</Eyebrow></div>}
    {title && <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white">{title}</h2>}
    {subtitle && <p className="mt-3 text-base md:text-lg text-white/60">{subtitle}</p>}
  </div>
);

export const Card = ({ children, className = '', hover = true }) => (
  <div
    className={cx(
      'rounded-2xl border border-white/10 bg-white/[0.04] p-6',
      hover && 'transition duration-200 hover:-translate-y-1 hover:border-white/20 hover:bg-white/[0.06]',
      className
    )}
  >
    {children}
  </div>
);

const BTN_BASE =
  'inline-flex items-center justify-center gap-2 rounded-full font-semibold transition duration-200 cursor-pointer text-center';
const BTN_SIZES = {
  md: 'px-6 py-3 text-base',
  lg: 'px-8 py-4 text-lg',
};
const BTN_VARIANTS = {
  primary: 'bg-brand text-white hover:bg-brand-light shadow-lg shadow-brand/20',
  secondary: 'border border-white/20 text-white hover:bg-white/10',
  ghost: 'text-white/80 hover:text-white',
};

export const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  to,
  href,
  onClick,
  className = '',
  ...rest
}) => {
  const classes = cx(BTN_BASE, BTN_SIZES[size], BTN_VARIANTS[variant], className);
  if (to) return <Link to={to} className={classes} onClick={onClick} {...rest}>{children}</Link>;
  if (href) return <a href={href} className={classes} onClick={onClick} {...rest}>{children}</a>;
  return <button type="button" className={classes} onClick={onClick} {...rest}>{children}</button>;
};

export default { Section, Eyebrow, SectionHeading, Card, Button };
